import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const createBdfLineKey = () =>
  `bdf-line-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const SALES_ORDER_CHARGE_LINE_TYPES = new Set([
  "AGENCY_COMMISSION",
  "TRANSPORTATION",
]);

/** Inventory (site) id — same for duplicate type-R lines. */
export const resolveInventoryId = (item) => {
  if (!item) return "";
  if (item.inventoryId) return String(item.inventoryId);
  const inv = item.invId;
  if (inv && typeof inv === "object") {
    return String(inv._id ?? inv.$oid ?? "");
  }
  if (inv != null && inv !== "") return String(inv);
  return String(item._id ?? "");
};

/** BDF sub-document line id from DB (unique per row in inventories array). */
export const resolveBdfLineId = (item) => {
  if (!item) return "";
  if (item.bdfInventoryLineId) return String(item.bdfInventoryLineId);
  if (item.bdfLineKey && !String(item.bdfLineKey).startsWith("bdf-line-")) {
    const invId = resolveInventoryId(item);
    if (String(item.bdfLineKey) !== invId) return String(item.bdfLineKey);
  }
  const invId = resolveInventoryId(item);
  const candidate = item._id != null ? String(item._id) : "";
  if (candidate && candidate !== invId) return candidate;
  return "";
};

export const getBdfLineKey = (item) =>
  item?.bdfLineKey ?? item?.bdfInventoryLineId ?? item?._id;

export const withBdfLineKey = (item) => {
  if (item?.bdfLineKey) return item;
  const key = createBdfLineKey();
  return { ...item, bdfLineKey: key };
};

export const formatBdfDateForUi = (dateStr) => {
  if (!dateStr) return dateStr;
  let parsed = dayjs.utc(dateStr, "DD-MM-YYYY", true);
  if (!parsed.isValid()) parsed = dayjs.utc(dateStr);
  if (!parsed.isValid()) parsed = dayjs(dateStr, "DD-MM-YYYY", true);
  if (!parsed.isValid()) parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : dateStr;
};

/**
 * After getBdfDataById / getDraft: keep unique line keys, inventory id for redux,
 * and DD-MM-YYYY dates so duplicate rows do not share React keys.
 */
export const normalizeBdfInventoriesFromApi = (inventories) => {
  if (!Array.isArray(inventories)) return [];

  // Agency Commission and Transportation are Sales Order charge lines, not
  // inventory records. Keep them out of BDF screens, which expect an invId.
  return inventories
    .filter((item) => !SALES_ORDER_CHARGE_LINE_TYPES.has(item?.lineType))
    .map((item) => {
      const inventoryId = resolveInventoryId(item);
      const lineId = resolveBdfLineId(item);
      const bdfLineKey = lineId || item.bdfLineKey || createBdfLineKey();

      return {
        ...item,
        inventoryId,
        invId: inventoryId,
        _id: inventoryId,
        bdfInventoryLineId: lineId || undefined,
        bdfLineKey,
        startDate: formatBdfDateForUi(item.startDate),
        endDate: formatBdfDateForUi(item.endDate),
      };
    });
};

export const enrichBdfInventoryFromRedux = (item, reduxInvs) => {
  if (!item) return item;
  const inventoryId = resolveInventoryId(item);
  const fromRedux = reduxInvs?.find((r) => r._id === inventoryId);
  if (!fromRedux) return item;

  return {
    ...item,
    Bookings: item.Bookings ?? fromRedux.Bookings,
    blockedDates: item.blockedDates ?? fromRedux.blockedDates,
    premiumBlock: item.premiumBlock ?? fromRedux.premiumBlock,
    isOverlapPossible:
      item.isOverlapPossible ?? fromRedux.isOverlapPossible ?? false,
    removedOn: item.removedOn ?? fromRedux.removedOn,
    isArchived: item.isArchived ?? fromRedux.isArchived,
    unit: item.unit ?? fromRedux.unit,
    typeOfMedia: item.typeOfMedia ?? fromRedux.typeOfMedia,
    fullId: item.fullId ?? fromRedux.fullId,
    half1Id: item.half1Id ?? fromRedux.half1Id,
    half2Id: item.half2Id ?? fromRedux.half2Id,
  };
};

export const ensureUniqueBdfLineKeys = (items) => {
  if (!Array.isArray(items) || items.length === 0) return items;

  const seen = new Set();
  let changed = false;

  const result = items.map((item) => {
    let key = item.bdfLineKey ?? item.bdfInventoryLineId;
    if (!key || seen.has(key)) {
      key = createBdfLineKey();
      changed = true;
      seen.add(key);
      return {
        ...item,
        bdfLineKey: key,
        bdfInventoryLineId: item.bdfInventoryLineId ?? key,
      };
    }
    seen.add(key);
    if (!item.bdfLineKey) {
      changed = true;
      return { ...item, bdfLineKey: key };
    }
    return item;
  });

  return changed ? result : items;
};

export const parseBdfDateYmd = (dateStr) => {
  if (!dateStr) return null;
  const strict = dayjs(dateStr, "DD-MM-YYYY", true);
  if (strict.isValid()) return strict.format("YYYY-MM-DD");
  const loose = dayjs(dateStr);
  return loose.isValid() ? loose.format("YYYY-MM-DD") : null;
};

export const isDateWithinBdfPeriod = (dateYmd, startStr, endStr) => {
  const start = parseBdfDateYmd(startStr);
  const end = parseBdfDateYmd(endStr);
  if (!dateYmd || !start || !end) return false;
  return dateYmd >= start && dateYmd <= end;
};

export const doBdfPeriodsOverlap = (startA, endA, startB, endB) => {
  const aStart = parseBdfDateYmd(startA);
  const aEnd = parseBdfDateYmd(endA);
  const bStart = parseBdfDateYmd(startB);
  const bEnd = parseBdfDateYmd(endB);
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
};

export const getSiblingLineBookingPeriods = (
  inventories,
  currentLineKey,
  inventoryId,
) => {
  if (!inventoryId || !Array.isArray(inventories)) return [];

  return inventories
    .filter(
      (item) =>
        resolveInventoryId(item) === inventoryId &&
        getBdfLineKey(item) !== currentLineKey &&
        item.startDate &&
        item.endDate,
    )
    .map((item) => ({
      startDate: item.startDate,
      endDate: item.endDate,
    }));
};

export const isDateBlockedBySiblingBdfLines = (
  dateYmd,
  inventories,
  currentLineKey,
  inventoryId,
) => {
  const periods = getSiblingLineBookingPeriods(
    inventories,
    currentLineKey,
    inventoryId,
  );
  return periods.some(({ startDate, endDate }) =>
    isDateWithinBdfPeriod(dateYmd, startDate, endDate),
  );
};

export const hasOverlapWithSiblingBdfLines = (
  inventories,
  currentLineKey,
  inventoryId,
  startDate,
  endDate,
) => {
  const periods = getSiblingLineBookingPeriods(
    inventories,
    currentLineKey,
    inventoryId,
  );
  return periods.some(({ startDate: s, endDate: e }) =>
    doBdfPeriodsOverlap(startDate, endDate, s, e),
  );
};
